import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { supabaseAdmin } from '../../../lib/supabase';
import { auditReportService } from '../../../services/auditReportService';
import toast from 'react-hot-toast';
import CustomerUserProfileModal from './CustomerUserProfileModal';

/* ─── Fix 3: parse Supabase timestamps correctly ─────────────────────────────
   Supabase returns timestamps WITHOUT a timezone suffix, e.g.
   "2026-02-17 12:24:28.912767"
   JS Date() treats that as local time, not UTC → wrong by your UTC offset.
   Appending 'Z' tells JS it's UTC, then we display in IST.
────────────────────────────────────────────────────────────────────────────── */
const parseSupabaseDate = (raw) => {
  if (!raw) return null;
  // Already has timezone info — use as-is
  if (raw.includes('+') || raw.endsWith('Z')) return new Date(raw);
  // No suffix → treat as UTC
  return new Date(raw.replace(' ', 'T') + 'Z');
};

const formatDate = (raw) => {
  const d = parseSupabaseDate(raw);
  if (!d || isNaN(d)) return '—';
  return d.toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    timeZone: 'Asia/Kolkata',
  });
};

const formatDateTime = (raw) => {
  const d = parseSupabaseDate(raw);
  if (!d || isNaN(d)) return '—';
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
};

const formatAmount = (amount, currency) => {
  if (!amount) return null;
  const value = amount / 100;
  return currency === 'INR'
    ? `₹${value.toLocaleString('en-IN')}`
    : `$${value.toLocaleString('en-US')}`;
};

const STATUS_RANK = { captured: 4, received: 3, pending: 2, failed: 1 };

/* ─── Payment pill ───────────────────────────────────────────────────────── */
const PaymentPill = ({ paymentStatus, customerType }) => {
  if (customerType === 'free' || !paymentStatus) {
    return (
      <span style={pill('#f8fafc','#e2e8f0','#94a3b8')}>
        <MinusIcon /> No Payment
      </span>
    );
  }
  if (paymentStatus === 'captured' || paymentStatus === 'received') {
    return (
      <span style={pill('#dcfce7','#86efac','#166534')}>
        <CheckIcon /> Received
      </span>
    );
  }
  if (paymentStatus === 'pending') {
    return (
      <span style={pill('#fefce8','#fde047','#854d0e')}>
        <SpinnerIcon /> Pending
      </span>
    );
  }
  return (
    <span style={pill('#fef2f2','#fca5a5','#dc2626')}>
      <XIcon /> Failed
    </span>
  );
};

const pill = (bg, border, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '3px 10px', borderRadius: 20,
  fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
  background: bg, border: `1px solid ${border}`, color,
});

const CheckIcon   = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>;
const MinusIcon   = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const XIcon       = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const SpinnerIcon = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>;

/* ─── Main Component ─────────────────────────────────────────────────────── */
const CustomerUserManagement = ({ onUserAction, onBulkAction }) => {
  const [users,           setUsers]           = useState([]);
  const [isLoading,       setIsLoading]       = useState(true);
  const [selectedUsers,   setSelectedUsers]   = useState([]);
  const [searchQuery,     setSearchQuery]     = useState('');
  const [statusFilter,    setStatusFilter]    = useState('all');
  const [sortBy,          setSortBy]          = useState('created_at');
  const [sortOrder,       setSortOrder]       = useState('desc');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [selectedProfileId, setSelectedProfileId] = useState(null);

  useEffect(() => { fetchCustomerUsers(); }, []);

  const fetchCustomerUsers = async () => {
    try {
      setIsLoading(true);

      /* ── profiles ── */
      const { data: profilesData, error: profilesError } = await supabaseAdmin
        ?.from('user_profiles')
        ?.select(`
          id,
          email,
          full_name,
          company_name,
          job_title,
          mobile,
          role,
          subscription_status,
          created_at
        `)
        ?.neq('role', 'admin');

      if (profilesError) throw profilesError;

      /* ── Fix 2: fetch submission counts separately ────────────────────────
         PostgREST aggregate `(count)` inside a select sometimes returns []
         when there are no rows, giving count=0 incorrectly. A dedicated
         count query per user is reliable. We batch it as one query grouped
         by user_id using a view or RPC — but since we can't guarantee that
         exists, we do a simple select of all non-admin submission user_ids
         and count client-side (only the ID column, lightweight).
      ────────────────────────────────────────────────────────────────────── */
      const { data: submissionsData, error: submissionsError } = await supabaseAdmin
        ?.from('deck_submissions')
        ?.select('user_id');

      if (submissionsError) console.error('Error fetching submissions:', submissionsError);

      // Build count map: user_id → number of submissions
      const submissionCountMap = {};
      (submissionsData || []).forEach(row => {
        submissionCountMap[row.user_id] = (submissionCountMap[row.user_id] || 0) + 1;
      });

      /* ── payments ── */
      const { data: paymentsData, error: paymentsError } = await supabaseAdmin
        ?.from('paid_audit_orders')
        ?.select('user_id, payment_status, razorpay_payment_id, amount, currency, paid_at')
        ?.order('paid_at', { ascending: false });

      if (paymentsError) console.error('Error fetching payments:', paymentsError);

      // Best payment per user (highest STATUS_RANK)
      const paymentMap = {};
      (paymentsData || []).forEach(p => {
        const existing     = paymentMap[p.user_id];
        const incomingRank = STATUS_RANK[p.payment_status] || 0;
        const existingRank = existing ? (STATUS_RANK[existing.payment_status] || 0) : -1;
        if (!existing || incomingRank > existingRank) paymentMap[p.user_id] = p;
      });

      const transformedUsers = (profilesData || []).map(profile => {
        const payment    = paymentMap[profile.id] || null;
        const isPaid     = payment?.payment_status === 'captured' || payment?.payment_status === 'received';

        return {
          id:               profile.id,
          email:            profile.email,
          /* Fix 4: use correct column names */
          fullName:         profile.full_name    || 'N/A',
          company:          profile.company_name || 'N/A',   // ← was profile.company
          jobTitle:         profile.job_title    || 'N/A',
          mobile:           profile.mobile       || null,
          registrationDate: profile.created_at,
          /* Fix 2: use client-side count map */
          submissionCount:  submissionCountMap[profile.id] || 0,
          status:           'active',
          role:             profile.role || 'user',
          /* Fix 1: derive subscription label from payment, not the stale DB column */
          subscriptionStatus: isPaid ? 'paid' : (profile.subscription_status || 'free'),
          customerType:     isPaid ? 'paid' : 'free',
          paymentStatus:      payment?.payment_status       || null,
          razorpayPaymentId:  payment?.razorpay_payment_id || null,
          paymentAmount:      payment?.amount               || null,
          paymentCurrency:    payment?.currency             || 'INR',
          paymentDate:        payment?.paid_at              || null,
        };
      });

      setUsers(transformedUsers);
    } catch (error) {
      console.error('Error fetching customer users:', error);
      toast.error('Failed to load customers');
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Options ── */
  const statusOptions = [
    { value: 'all',      label: 'All Status' },
    { value: 'active',   label: 'Active'     },
    { value: 'inactive', label: 'Inactive'   },
    { value: 'pending',  label: 'Pending'    },
  ];
  const sortOptions = [
    { value: 'created_at',      label: 'Registration Date' },
    { value: 'email',           label: 'Email'             },
    { value: 'submissionCount', label: 'Submissions'       },
  ];

  /* ── Filter + sort ── */
  const filteredUsers = users
    .filter(user => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        user.email?.toLowerCase().includes(q) ||
        user.company?.toLowerCase().includes(q) ||
        user.fullName?.toLowerCase().includes(q) ||
        user.jobTitle?.toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let cmp = 0;
      if      (sortBy === 'created_at')      cmp = parseSupabaseDate(a.registrationDate) - parseSupabaseDate(b.registrationDate);
      else if (sortBy === 'email')           cmp = a.email?.localeCompare(b.email);
      else if (sortBy === 'submissionCount') cmp = a.submissionCount - b.submissionCount;
      return sortOrder === 'asc' ? cmp : -cmp;
    });

  /* ── Handlers ── */
  const handleSelectAll  = (checked) => setSelectedUsers(checked ? filteredUsers.map(u => u.id) : []);
  const handleSelectUser = (id, checked) =>
    setSelectedUsers(checked ? [...selectedUsers, id] : selectedUsers.filter(x => x !== id));

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('desc'); }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await auditReportService?.deleteUser(userId);
      toast.success('Customer deleted successfully', { icon: '🗑️' });
      setDeleteConfirmId(null);
      await fetchCustomerUsers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Failed to delete customer');
    }
  };

  /* ── Badges ── */
  const getStatusBadge = (status) => {
    const cfg = {
      active:   { color: 'bg-success/10 text-success',       label: 'Active'   },
      inactive: { color: 'bg-muted text-muted-foreground',   label: 'Inactive' },
      pending:  { color: 'bg-warning/10 text-warning',       label: 'Pending'  },
    };
    const c = cfg[status] || cfg.pending;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.color}`}>{c.label}</span>;
  };

  /* Fix 1: badge now reflects actual payment status, not the stale DB column */
  const getCustomerTypeBadge = (user) => {
    const isPaid = user.customerType === 'paid';
    const cfg = isPaid
      ? { color: 'bg-primary/10 text-primary border border-primary/20', label: 'Paid', icon: 'Crown'   }
      : { color: 'bg-muted text-muted-foreground border border-border',  label: 'Free', icon: 'Package' };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color} flex items-center gap-1.5 w-fit`}>
        <Icon name={cfg.icon} size={12} />
        {cfg.label}
        {/* Show the actual subscription_status from DB as secondary info */}
        {isPaid && user.subscriptionStatus !== 'paid' && (
          <span className="opacity-60 font-normal">(DB: {user.subscriptionStatus})</span>
        )}
      </span>
    );
  };

  /* ── Shared customer info block ── */
  const CustomerInfoBlock = ({ user }) => (
    <div className="flex flex-col">
      <span className="text-sm font-medium text-foreground">{user.fullName}</span>
      <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
        <Icon name="Mail" size={12} />{user.email}
      </span>
      {/* Fix 4: company_name and job_title now correctly mapped */}
      {user.company !== 'N/A' && (
        <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
          <Icon name="Building2" size={12} />{user.company}
        </span>
      )}
      {user.jobTitle !== 'N/A' && (
        <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
          <Icon name="Briefcase" size={12} />{user.jobTitle}
        </span>
      )}
      {user.mobile && (
        <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
          <Icon name="Phone" size={12} />{user.mobile}
        </span>
      )}
    </div>
  );

  /* ── Payment detail block (desktop + mobile) ── */
  const PaymentDetail = ({ user }) => (
    <>
      <PaymentPill paymentStatus={user.paymentStatus} customerType={user.customerType} />
      {user.customerType === 'paid' && (
        <div className="mt-1 space-y-0.5">
          {user.razorpayPaymentId && (
            <p style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'ui-monospace,monospace' }}>
              {user.razorpayPaymentId}
            </p>
          )}
          {user.paymentAmount && (
            <p style={{ fontSize: 12, fontWeight: 600, color: '#166534' }}>
              {formatAmount(user.paymentAmount, user.paymentCurrency)}
            </p>
          )}
          {user.paymentDate && (
            /* Fix 3: use parseSupabaseDate for display */
            <p style={{ fontSize: 11, color: '#94a3b8' }}>{formatDate(user.paymentDate)}</p>
          )}
        </div>
      )}
    </>
  );

  /* ── Action buttons ── */
  const ActionButtons = ({ user, mobile = false }) => {
    const variant = mobile ? 'outline' : 'ghost';
    return (
      <div className={`flex items-center gap-2 ${mobile ? 'flex-wrap' : ''}`}>
        <Button variant={variant} size="sm" iconName="Eye"  onClick={() => setSelectedProfileId(user.id)}>View Profile</Button>
        <Button variant={variant} size="sm" iconName="Mail" onClick={() => onUserAction?.('email', user.id)}>Email</Button>
        {deleteConfirmId === user.id ? (
          <>
            <Button variant="destructive" size="sm" iconName="Trash2" onClick={() => handleDeleteUser(user.id)}>Confirm</Button>
            <Button variant="ghost"       size="sm" iconName="X"      onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          </>
        ) : (
          <Button variant="ghost" size="sm" iconName="Trash2" onClick={() => setDeleteConfirmId(user.id)} />
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg shadow-md border border-border p-8">
        <div className="flex items-center justify-center">
          <Icon name="Loader2" className="w-6 h-6 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading customer users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-md border border-border">

      {/* ── Header ── */}
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
              <Icon name="Users" size={20} className="text-primary" />
              Customer User Management
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {users.length} registered customer{users.length !== 1 ? 's' : ''} ·{' '}
              {users.filter(u => u.customerType === 'paid').length} paid
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="search"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full sm:w-64"
            />
            <Select options={statusOptions} value={statusFilter} onChange={setStatusFilter} className="w-full sm:w-40" />
            <Select options={sortOptions}   value={sortBy}       onChange={setSortBy}       className="w-full sm:w-48" />
          </div>
        </div>

        {selectedUsers.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 p-3 bg-muted rounded-lg">
            <span className="text-sm text-foreground font-medium">{selectedUsers.length} selected</span>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" iconName="CheckCircle2" onClick={() => onBulkAction?.('activate',   selectedUsers)}>Activate</Button>
              <Button variant="outline" size="sm" iconName="XCircle"      onClick={() => onBulkAction?.('deactivate', selectedUsers)}>Deactivate</Button>
              <Button variant="outline" size="sm" iconName="Mail"         onClick={() => onBulkAction?.('email',      selectedUsers)}>Send Email</Button>
              <Button variant="ghost"   size="sm" iconName="X"            onClick={() => setSelectedUsers([])}>Clear</Button>
            </div>
          </div>
        )}
      </div>

      {/* ── Desktop Table ── */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left">
                <Checkbox
                  checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              {[
                { label: 'Customer Info',      field: 'email'          },
                { label: 'Registered',         field: 'created_at'     },
                { label: 'Decks Uploaded',     field: 'submissionCount'},
                { label: 'Status',             field: null             },
                { label: 'Plan',               field: null             },
                { label: 'Payment',            field: null             },
                { label: 'Actions',            field: null             },
              ].map(({ label, field }) => (
                <th
                  key={label}
                  className={`px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider ${field ? 'cursor-pointer hover:text-foreground' : ''}`}
                  onClick={() => field && handleSort(field)}
                >
                  <div className="flex items-center gap-2">
                    {label}
                    {field && sortBy === field && (
                      <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {filteredUsers.map(user => (
              <tr key={user.id} className="hover:bg-muted/30 transition-smooth">
                <td className="px-6 py-4">
                  <Checkbox
                    checked={selectedUsers.includes(user.id)}
                    onChange={(e) => handleSelectUser(user.id, e?.target?.checked)}
                  />
                </td>
                <td className="px-6 py-4"><CustomerInfoBlock user={user} /></td>
                {/* Fix 3: correct UTC parsing */}
                <td className="px-6 py-4 text-sm text-foreground">{formatDateTime(user.registrationDate)}</td>
                {/* Fix 2: real count from submissionCountMap */}
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Icon name="FileText" size={16} className="text-primary" />
                    <span className="text-sm font-semibold text-foreground">{user.submissionCount}</span>
                    <span className="text-xs text-muted-foreground">deck{user.submissionCount !== 1 ? 's' : ''}</span>
                  </div>
                </td>
                <td className="px-6 py-4">{getStatusBadge(user.status)}</td>
                {/* Fix 1: badge derived from payment, not stale DB column */}
                <td className="px-6 py-4">{getCustomerTypeBadge(user)}</td>
                <td className="px-6 py-4" style={{ minWidth: 200 }}><PaymentDetail user={user} /></td>
                <td className="px-6 py-4"><ActionButtons user={user} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Mobile Cards ── */}
      <div className="lg:hidden divide-y divide-border">
        {filteredUsers.map(user => (
          <div key={user.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-3 flex-1">
                <Checkbox
                  checked={selectedUsers.includes(user.id)}
                  onChange={(e) => handleSelectUser(user.id, e?.target?.checked)}
                />
                <div className="flex-1 min-w-0">
                  <CustomerInfoBlock user={user} />
                </div>
              </div>
              {getStatusBadge(user.status)}
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
              <div>
                <span className="text-muted-foreground text-xs">Registered</span>
                {/* Fix 3 */}
                <p className="text-foreground font-medium text-xs mt-0.5">{formatDateTime(user.registrationDate)}</p>
              </div>
              <div>
                <span className="text-muted-foreground text-xs flex items-center gap-1"><Icon name="FileText" size={12} />Decks</span>
                {/* Fix 2 */}
                <p className="text-foreground font-semibold mt-0.5">{user.submissionCount}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-3 flex-wrap">
              {/* Fix 1 */}
              {getCustomerTypeBadge(user)}
              <PaymentPill paymentStatus={user.paymentStatus} customerType={user.customerType} />
            </div>

            {user.customerType === 'paid' && (user.razorpayPaymentId || user.paymentAmount) && (
              <div className="mb-3 p-2 bg-muted/30 rounded-lg text-xs space-y-1">
                {user.razorpayPaymentId && (
                  <p className="font-mono text-muted-foreground">{user.razorpayPaymentId}</p>
                )}
                {user.paymentAmount && (
                  <p className="font-semibold text-green-700">{formatAmount(user.paymentAmount, user.paymentCurrency)}</p>
                )}
                {user.paymentDate && (
                  <p className="text-muted-foreground">{formatDate(user.paymentDate)}</p>
                )}
              </div>
            )}

            <ActionButtons user={user} mobile />
          </div>
        ))}
      </div>

      {filteredUsers.length === 0 && (
        <div className="p-8 md:p-12 text-center">
          <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-muted-foreground">No customers found matching your criteria</p>
        </div>
      )}

      {selectedProfileId && (
        <CustomerUserProfileModal
          userId={selectedProfileId}
          onClose={() => setSelectedProfileId(null)}
        />
      )}
    </div>
  );
};

export default CustomerUserManagement;